import { useCurrentUser } from 'FrontRoyalAngular';
import { useMemo } from 'react';
import { getCohort } from 'Users';
import { isCapstoneProject } from './learnerProjects';
import { type LearnerProject, type ProjectsForPlaylists } from './LearnerProjects.types';

// All three outputs are derived from `cohort.projectUnlockData` (Rails-computed) by matching each
// entry to its full LearnerProject object in `cohort.learnerProjects`. The previous client-side
// walk over `cohort.periods` (and the older walk-back/walk-forward) is gone — both the unlock
// period and the associated playlist now come straight from
// Cohort::ProjectUnlockDates#project_unlock_data. See PR #16997 and
// https://trello.com/c/euiBR0PP.
export const useProjectsForPlaylists = (): ProjectsForPlaylists => {
    const user = useCurrentUser();
    const cohort = getCohort(user);

    return useMemo(() => {
        const projectsByPlaylist: Record<string, LearnerProject[]> = {};
        const capstones: LearnerProject[] = [];
        const periodIndexForProjectUnlock: Record<string, number> = {};

        if (!cohort) return { projectsByPlaylist, capstones, periodIndexForProjectUnlock };

        cohort.projectUnlockData?.forEach(entry => {
            periodIndexForProjectUnlock[entry.requirementIdentifier] = entry.unlockPeriodIndex;

            // Guard against payload skew where projectUnlockData references a requirement
            // identifier that is not present in learnerProjects.
            const project = cohort.learnerProjects?.find(p => p.requirementIdentifier === entry.requirementIdentifier);
            if (!project) return;

            if (isCapstoneProject(project)) capstones.push(project);

            if (entry.associatedPlaylistLocalePackId) {
                // `?? []` because reading an unset key on a Record<string, T[]> returns undefined
                // at runtime (the type annotation hides this), and `[...undefined, project]` throws.
                projectsByPlaylist[entry.associatedPlaylistLocalePackId] = [
                    ...(projectsByPlaylist[entry.associatedPlaylistLocalePackId] ?? []),
                    project,
                ];
            }
        });

        return { projectsByPlaylist, capstones, periodIndexForProjectUnlock };
    }, [cohort]);
};

export const useProjectsForPlaylist = (localePackId: string) => {
    const { projectsByPlaylist } = useProjectsForPlaylists();
    return projectsByPlaylist[localePackId] ?? [];
};

export const usePeriodIndexForProjectUnlock = (requirementIdentifier: string): number => {
    const { periodIndexForProjectUnlock } = useProjectsForPlaylists();
    return periodIndexForProjectUnlock[requirementIdentifier] ?? -1;
};
